import { useMemo } from 'react';
import type { Edge, Node } from '@xyflow/react';
import {
  countTriggerNodes,
  findFanoutWarnings,
  findFloatingNodes,
  findTaskAnchorBindingErrors,
  findUnconnectedRequiredSockets,
} from '@erp/workflows';
import {
  edgesToValidationEdges,
  findOrphanSocketEdges,
  nodesToValidationSteps,
} from './editorUtils';

export type EditorValidationState = ReturnType<typeof useEditorValidationState>;

/** Messages per node, for findings that name the nodes they are about. */
export function groupMessagesByNode(
  findings: ReadonlyArray<{ nodeIds?: string[]; message: string }>,
): Map<string, string[]> {
  const byNode = new Map<string, string[]>();
  for (const finding of findings) {
    for (const nodeId of finding.nodeIds ?? []) {
      const current = byNode.get(nodeId) ?? [];
      current.push(finding.message);
      byNode.set(nodeId, current);
    }
  }
  return byNode;
}

/** Socket ids per step, for findings that point at specific inputs. */
export function groupSocketIdsByStep(
  sockets: ReadonlyArray<{ stepId: string; socketId: string }>,
): Map<string, Set<string>> {
  const byStep = new Map<string, Set<string>>();
  for (const socket of sockets) {
    const current = byStep.get(socket.stepId) ?? new Set<string>();
    current.add(socket.socketId);
    byStep.set(socket.stepId, current);
  }
  return byStep;
}

export function useEditorValidationState(nodes: Node[], edges: Edge[]) {
  // react-doctor-disable-next-line react-doctor/react-compiler-no-manual-memoization
  return useMemo(() => {
    const validationSteps = nodesToValidationSteps(nodes);
    const validationEdges = edgesToValidationEdges(edges);
    const floatingNodeIds = new Set(
      findFloatingNodes(validationSteps, validationEdges),
    );
    const unconnectedSockets = findUnconnectedRequiredSockets(
      validationSteps,
      validationEdges,
    );
    const orphanEdges = findOrphanSocketEdges(nodes, edges);

    // Blocking, and shown live: a task step whose wired anchor entity and
    // selected task type disagree fails on every run, so the node is outlined
    // as soon as the wiring or the type picker puts it in that state — not
    // only when publish is attempted.
    const taskAnchorErrors = findTaskAnchorBindingErrors(
      validationSteps,
      validationEdges,
    );

    // Non-blocking. These graphs are valid and run correctly; they just scale
    // in a way that only shows up when an unusually large input arrives.
    const fanoutWarnings = findFanoutWarnings(validationSteps, validationEdges);

    return {
      floatingNodeIds,
      orphanEdges,
      unconnectedSocketsByStep: groupSocketIdsByStep(unconnectedSockets),
      taskAnchorErrors,
      taskAnchorErrorsByStep: groupMessagesByNode(taskAnchorErrors),
      taskAnchorErrorSocketsByStep: groupSocketIdsByStep(
        taskAnchorErrors.flatMap((error) => error.sockets ?? []),
      ),
      hasTaskAnchorErrors: taskAnchorErrors.length > 0,
      fanoutWarnings,
      fanoutWarningsByStep: groupMessagesByNode(fanoutWarnings),
      hasFanoutWarnings: fanoutWarnings.length > 0,
      triggerCount: countTriggerNodes(validationSteps),
      hasFloatingNodes: floatingNodeIds.size > 0,
      hasUnconnectedRequiredSockets: unconnectedSockets.length > 0,
      hasOrphanEdges: orphanEdges.length > 0,
      hasScheduleTrigger: nodes.some(
        (node) =>
          (node.data as { stepType?: string }).stepType === 'schedule_trigger',
      ),
    };
  }, [nodes, edges]);
}
